import { Box, Checkbox, Typography } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { ConfirmationDialogResult, InstantMessageChannelUserActiveState } from "ui/Scripts/source/models/enums";
import { InstantMessageChannelAddUsersSuccess } from "ui/Scripts/source/models/instantMessage/instantMessageChannelAddUsersSuccess";
import { IInstantMessageChannelAddedUser, InstantMessageChannelAddedUser } from "ui/Scripts/source/models/instantMessage/instantMessageChannelAddedUser";
import { IInstantMessageChannelEditUserItem, InstantMessageChannelEditUserItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelUserEditItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as instantMessageChannelService from "../../../models/services/instantMessageChannelService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUserListItem } from "../../../models/user/userListItem";
import { UserListItems } from "../../../models/user/userListItems";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { IListValueLineData } from "../../framework/List/ListLine";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { SelectUserDialogWithCompanySelect } from "../../user/SelectUserDialog/SelectUserDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

// InstantMessageChannelEditDialogUserList
export interface IInstantMessageChannelEditDialogUserListProp {
    newChannelWizard?: boolean;
    sessionState: ISessionState;
    isReadOnly: boolean;
    instantMessageChannelId: string;
    instantMessageChannelCompanyId: string;
    companies: ICompanyMenuItem[];
    users: IInstantMessageChannelEditUserItem[];
    
    onRemoved: (ids: string[]) => void;
    onAdded: (companyId :string, users: IUserListItem[], addedUsers: IInstantMessageChannelAddedUser[]) => void;
    onUserTypeUpdated: (id: string, userType: number) => void;
}

interface IInstantMessageChannelEditDialogUserListState {
    // List
    isLoading: boolean;
    users: IInstantMessageChannelEditUserItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectUserDialog: boolean;
}

export class InstantMessageChannelEditDialogUserList extends React.Component<IInstantMessageChannelEditDialogUserListProp, IInstantMessageChannelEditDialogUserListState> {
    getFilteredUsers = (filter: string, column: string, asc: boolean): IInstantMessageChannelEditUserItem[] => {
        const props = this.props;
        let users = props.users ? props.users.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            users = users.filter(i => i.userCode.toLowerCase().indexOf(lowFilter) > -1 || i.firstName.toLowerCase().indexOf(lowFilter) > -1 || i.lastName.toLowerCase().indexOf(lowFilter) > -1);
        }
        InstantMessageChannelEditUserItem.sortInstantMessageChannelEditUserItems(users, column, asc);
        return users;
    };

    constructor(props: IInstantMessageChannelEditDialogUserListProp) {
        super(props);
        this.state = {
            isLoading: false,
            users: this.getFilteredUsers("", "code", true),
            filter: "",
            sortColumn: "code",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectUserDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            users: this.getFilteredUsers(filter, column, asc),
        });
    };

    componentDidUpdate(prevProps: IInstantMessageChannelEditDialogUserListProp, prevState: IInstantMessageChannelEditDialogUserListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.users ? prevProps.users.map(i => i.getHash()) : [], props.users ? props.users.map(i => i.getHash()) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    // #region Set Active User Type
    getSetUserTypeSaveData = (props: IInstantMessageChannelEditDialogUserListProp, userId: string, userType: number): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.instantMessageChannelId);
        result.append("userId", userId);
        result.append("userType", userType);
        return result;
    };

    handleSetUserType = async (userId: string, userType: number) => {
        const props = this.props;
        const users = props.users.map(i => new InstantMessageChannelEditUserItem(i));
        const user = users.find(i => i.id === userId);
        if (!user) return;
        user.userType = userType;
        if (!users.find(i => i.getIsActive() && i.userType > 0.5)) {
            AppUtils.showErrorMessage(Translations.InstantMessageChannelMustHaveAtLeastOneAdministrator);
            return;
        }
        if (userType < 0.5 && userId === props.sessionState.id) {
            if (await AppUtils.showConfirmationDialog(Translations.InstantMessageChannelRemoveFromYourselfFromAdministratorsConfirmation) !== ConfirmationDialogResult.Yes) {
                return;
            }
        }
        const saveData = this.getSetUserTypeSaveData(props, userId, userType);
        // Call server
        if (!props.newChannelWizard) {
            const result = await AppUtils.callService(() => instantMessageChannelService.instantMessageChannelEditSetUserType(saveData.obj));
            if (!result) return;
        }
        props.onUserTypeUpdated(userId, userType);
    };
    // #endregion Set Active User Type

    // #region Remove Users
    getRemoveSaveData = (instantMessageChannelId: string, userIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", instantMessageChannelId);
        result.append("userIds", userIds);
        return result;
    };
    
    removeUsersFromInstantMessageChannel = (instantMessageChannelId: string, userIds: string[], showSuccessMessage: boolean = true, disableLoadIndicator: boolean = false): Promise<ApiSuccess> => {
        const saveData = this.getRemoveSaveData(instantMessageChannelId, userIds);
        return AppUtils.callService(() => instantMessageChannelService.instantMessageChannelEditRemoveUsers(saveData.obj), showSuccessMessage, disableLoadIndicator);
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        // Call server
        if (!props.newChannelWizard) {
            const users = props.users.filter(i => selectedIds.indexOf(i.id) < 0);
            if (!users.find(i => i.userType > 0.5)) {
                AppUtils.showErrorMessage(Translations.InstantMessageChannelMustHaveAtLeastOneAdministrator);
                return;
            }
            // Call server
            const result = await this.removeUsersFromInstantMessageChannel(props.instantMessageChannelId, selectedIds);
            if (!result) return;
        }
        this.setState({
            selectedId: selectedIds.indexOf(state.selectedId) < 0 ? state.selectedId : "",
            checkedIds: state.checkedIds.filter(i => selectedIds.indexOf(i) < 0)
        });
        props.onRemoved(selectedIds);
    };
    // #endregion Remove Users
    
    // #region Add Users
    handleAdd = () => {
        this.setState({
            showSelectUserDialog: true
        });
    };

    getAddSaveData = (instantMessageChannelId: string, userIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", instantMessageChannelId);
        result.append("userIds", userIds);
        return result;
    };

    addUsersToInstantMessageChannel = (instantMessageChannelId: string, userIds: string[], showSuccessMessage: boolean = true, disableLoadIndicator: boolean = false): Promise<InstantMessageChannelAddUsersSuccess> => {
        const saveData = this.getAddSaveData(instantMessageChannelId, userIds);
        return AppUtils.callService(() => instantMessageChannelService.instantMessageChannelEditAddUsers(saveData.obj), showSuccessMessage, disableLoadIndicator);
    };

    handleSelectUserDialogOk = async (users: IUserListItem[], companyId :string) => {
        const props = this.props;
        if (!users || users.length < 1) return;
        this.setState({
            showSelectUserDialog: false
        });
        // Call server
        let result: InstantMessageChannelAddUsersSuccess;
        if (!props.newChannelWizard) {
            result = await this.addUsersToInstantMessageChannel(props.instantMessageChannelId, users.map(i => i.id));
            if (!result) return;
        } else {
            result = new InstantMessageChannelAddUsersSuccess();
            result.addedUsers = users.map(i => InstantMessageChannelAddedUser.createInstantMessageChannelAddedUser(Base.getGuid(), i.id, InstantMessageChannelUserActiveState.Invited));
        }
        props.onAdded(companyId, users, result.addedUsers);
    };

    handleSelectUserDialogCancel = () => {
        this.setState({
            showSelectUserDialog: false
        });
    };

    handleGetUserSelectListItems = (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, ignoreQrCodeUsers: boolean, requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        const props = this.props;
        return instantMessageChannelService.instantMessageChannelGetSelectUserListItems(props.instantMessageChannelCompanyId, props.instantMessageChannelId, companyId, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode, sortColumn, sortOrderIsAsc);
    };
    // #endregion Add Users

    render() {
        const props = this.props;
        const state = this.state;
        const externalChannel = props.companies && props.companies.length > 1;
        let ignoreUserCodes = props.users.map(i => i.userCode);
        if (props.newChannelWizard) {
            ignoreUserCodes = ignoreUserCodes.concat([props.sessionState.code]);
        }
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            margin="1rem .5rem 1rem 1rem"
                            leftItems={
                                !props.newChannelWizard &&
                                    <ListSearchFilter
                                        searchFilter={state.filter}
                                        onSearchClick={this.changeFilter}
                                    />
                            }
                            rightItems={
                                <>
                                    <ListIconButtonsItem
                                        addTooltip={Translations.InviteInstantMessageChannelUser}
                                        removeTooltip={Translations.RemoveInstantMessageChannelUser}
                                        removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                        onAdd={!props.isReadOnly ? this.handleAdd : null}
                                        onRemove={!props.isReadOnly ? this.handleRemove : null}
                                    />
                                </>
                            }
                        />
                    </>}
                    main={<>
                        <List
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            globalLoadingInUse={false}
                            height="100%"
                            header={{
                                columns: [
                                    { size: props.newChannelWizard ? 12 : 9, column: "code", content: Translations.Name },
                                    props.newChannelWizard ? null : { size: 3, column: "name", content: Translations.InstantMessageChannelAdministrator }
                                ]
                            }}
                            lines={(() => {
                                const lines: IListValueLineData[] = [];
                                for (const company of props.companies) {
                                    const companyUsers = externalChannel
                                        ? state.users.filter(i => i.companyId === company.id)
                                        : state.users;
                                    if (companyUsers.length < 1) continue;
                                    //Company row
                                    if (externalChannel) {
                                        lines.push({
                                            columns: [
                                                {
                                                    size: 12,
                                                    content:
                                                        <Box display="flex" alignItems="center">
                                                            <AvatarImage
                                                                name={company.name}
                                                                image={company.logo}
                                                                tooltip={company.name}
                                                                marginRight=".5rem"
                                                            />
                                                            <Typography>{company.name}</Typography>
                                                        </Box>
                                                }
                                            ],
                                            id: company.id,
                                            classes: "company",
                                            unselectable: true,
                                        });
                                    }
                                    //User rows
                                    for (const user of companyUsers) {
                                        const userIsInvited = user.getIsInvited();
                                        const userIsRejected = user.getIsRejected();
                                        lines.push({
                                            columns: [
                                                {
                                                    size: props.newChannelWizard ? 12 : 9,
                                                    content:
                                                        <Box display="flex" alignItems="center" sx={{ paddingLeft: ".5rem" }}>
                                                            {userIsInvited &&
                                                                <span className="icon pending" title={Translations.WaitingToBeApproved}></span>
                                                            }
                                                            {userIsRejected &&
                                                                <span className="icon rejected" title={Translations.Rejected}></span>
                                                            }
                                                            <AvatarImage
                                                                id={user.userCode}
                                                                name={user.getName()}
                                                                image={user.picture}
                                                                tooltip={user.getName()}
                                                                marginRight=".5rem"
                                                                marginLeft={userIsInvited || userIsRejected ? "1rem" : undefined}
                                                            />
                                                            <Typography>{user.getName()}</Typography>
                                                        </Box>
                                                },
                                                props.newChannelWizard
                                                    ? null
                                                    : {
                                                        size: 3,
                                                        content:
                                                            <div className="flexXYCenter">
                                                                {(props.isReadOnly || externalChannel && user.companyId !== props.instantMessageChannelCompanyId
                                                                    ? (user.userType > 0 ? <span className="icon chircleCheck"></span> : null)
                                                                    : <Checkbox
                                                                        checked={user.userType > 0}
                                                                        disabled={!user.getIsActive()}
                                                                        onChange={(_e, checked: boolean) => {
                                                                            this.handleSetUserType(user.id, checked ? 1 : 0);
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>
                                                    }
                                            ],
                                            id: user.id,
                                            classes: "user" + (externalChannel ? " hasCompanyLine" : "") + (user.getIsActive() ? "" : " inActive")
                                        });
                                    }
                                }
                                return lines;
                            })()}
                            noDataMessage={Translations.AddAtleastOneInstantMessageChannelUsersToInstantMessageChannel}
                            noDataMessageThreshold={props.users.length < (props.newChannelWizard ? 1 : 2) ? 1000 : 0}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                        />
                    </>}
                />
                {state.showSelectUserDialog &&
                    <SelectUserDialogWithCompanySelect
                        title={Translations.InviteInstantMessageChannelUser}
                        companies={props.companies}
                        showUserGroupsCompanyId={props.instantMessageChannelCompanyId}
                        ignoreUserCodes={ignoreUserCodes}
                        multiSelect={true}
                        onOk={this.handleSelectUserDialogOk}
                        onCancel={this.handleSelectUserDialogCancel}
                        onGetUserSelectListItems={this.handleGetUserSelectListItems}
                    />
                }
            </>
        );
    }
}